import { onUnmounted, watch } from "vue";
import {
  isJust,
  isNothing,
  just,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";

/**
 * Report an element's width to `onWidth` while it is mounted (ZW-276).
 *
 * The first measurement is sent at once so a narrow window opens straight
 * into the narrow layout. Later resizes are debounced so dragging a window
 * edge does not flip the layout on every frame.
 */
export function useShellWidth(
  el: () => Maybe<HTMLElement>,
  onWidth: (width: number) => void,
  debounceMs = 150,
): void {
  let observer: Maybe<ResizeObserver> = nothing;
  let timer: Maybe<ReturnType<typeof setTimeout>> = nothing;
  let measured = false;

  function clearTimer(): void {
    if (isJust(timer)) clearTimeout(timer.value);
    timer = nothing;
  }

  function stop(): void {
    clearTimer();
    if (isJust(observer)) observer.value.disconnect();
    observer = nothing;
    measured = false;
  }

  function report(width: number): void {
    if (measured === false) {
      measured = true;
      onWidth(width);
      return;
    }
    clearTimer();
    timer = just(
      setTimeout(() => {
        timer = nothing;
        onWidth(width);
      }, debounceMs),
    );
  }

  watch(
    el,
    (node) => {
      stop();
      if (isNothing(node) || typeof ResizeObserver === "undefined") return;
      const next = new ResizeObserver((entries) => {
        const entry = toMaybe(entries[entries.length - 1]);
        if (isJust(entry)) report(entry.value.contentRect.width);
      });
      next.observe(node.value);
      observer = just(next);
    },
    { immediate: true, flush: "post" },
  );

  onUnmounted(stop);
}
